import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Button, Chip, Collapse, Tooltip, Typography, makeStyles } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import type { SpecIntelligenceFinding } from 'api/graphQL/types/specIntelligence.types'
import clsx from 'clsx'
import React, { useMemo, useState } from 'react'
import { CATEGORY_COLORS, CATEGORY_DISPLAY_LABELS, SPEC_INT_AI_COLOR } from './specIntelligenceConstants'

/** Explains the AI badge shown on findings produced by AI-powered analysis. */
const AI_BADGE_TOOLTIP = 'This finding was identified by AI-powered analysis.'

const DEFAULT_CATEGORY_STYLE = { backgroundColor: '#E0E0E0', color: '#424242' }

/** Matches paragraph references such as "[[1.2 > A.]]" embedded in finding descriptions. */
const PARAGRAPH_REF_PATTERN = /\[\[(.+?)\]\]/g

/** Splits a description on paragraph-reference tokens, rendering each as a bold span. */
const renderDescriptionWithParagraphRefs = (description: string, highlightClassName: string): React.ReactNode[] =>
    description.split(PARAGRAPH_REF_PATTERN).map((part, index) =>
        index % 2 === 1 ? <span key={index} className={highlightClassName}>{part}</span> : part
    )

const useStyles = makeStyles(() => ({
    card: {
        borderBottom: '1px solid #EEEEEE',
        padding: `${pxToRem(12)} ${pxToRem(12)} ${pxToRem(8)}`,
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#FAFAFA' },
    },
    headerRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: pxToRem(8),
        marginBottom: pxToRem(4),
    },
    sectionInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: pxToRem(4),
        minWidth: 0,
        flex: 1,
    },
    sectionNumber: {
        fontSize: pxToRem(11),
        fontWeight: 600,
        color: '#424242',
        whiteSpace: 'nowrap',
    },
    sectionDot: { fontSize: pxToRem(11), color: '#9E9E9E' },
    sectionName: {
        fontSize: pxToRem(11),
        color: '#757575',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    headerRight: {
        display: 'flex',
        alignItems: 'center',
        gap: pxToRem(4),
        flexShrink: 0,
    },
    categoryChip: {
        height: pxToRem(20),
        fontSize: pxToRem(10),
        '& .MuiChip-label': { padding: `0 ${pxToRem(6)}` },
    },
    aiBadge: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        height: pxToRem(18),
        borderRadius: pxToRem(4),
        padding: `0 ${pxToRem(6)}`,
        backgroundColor: '#EDE7F6',
        color: SPEC_INT_AI_COLOR,
        fontSize: pxToRem(10),
        fontWeight: 700,
        cursor: 'default',
    },
    expandIcon: {
        fontSize: pxToRem(18),
        color: '#757575',
        transition: 'transform 200ms',
    },
    expandIconOpen: { transform: 'rotate(180deg)' },
    description: {
        fontSize: pxToRem(13),
        color: '#757575',
        lineHeight: 1.5,
    },
    referencedSection: {
        color: '#212121',
        fontWeight: 500,
    },
    actions: {
        display: 'flex',
        gap: pxToRem(8),
        marginTop: pxToRem(10),
    },
    resolveButton: {
        flex: 1,
        textTransform: 'none' as const,
        fontSize: pxToRem(12),
        fontWeight: 500,
        backgroundColor: '#04325B',
        color: '#FFFFFF',
        '&:hover': { backgroundColor: '#02264A' },
    },
    dismissButton: {
        flex: 1,
        textTransform: 'none' as const,
        fontSize: pxToRem(12),
        fontWeight: 500,
        borderColor: '#9E9E9E',
        color: '#424242',
    },
}))

interface FindingCardHeaderProps {
    finding: SpecIntelligenceFinding
    expanded: boolean
}

const FindingCardHeader = ({ finding, expanded }: FindingCardHeaderProps) => {
    const classes = useStyles()
    const displayCategory = CATEGORY_DISPLAY_LABELS[finding.category] ?? finding.category
    const chipStyle = CATEGORY_COLORS[finding.category] ?? DEFAULT_CATEGORY_STYLE
    return (
        <div className={classes.headerRow}>
            <div className={classes.sectionInfo}>
                <Typography component="span" className={classes.sectionNumber}>
                    {finding.sectionNumber}
                </Typography>
                <Typography component="span" className={classes.sectionDot}>·</Typography>
                <Typography component="span" className={classes.sectionName}>
                    {finding.sectionName}
                </Typography>
            </div>
            <div className={classes.headerRight}>
                <Chip
                    label={displayCategory}
                    size="small"
                    className={classes.categoryChip}
                    style={chipStyle}
                />
                {finding.isAI && (
                    <Tooltip title={AI_BADGE_TOOLTIP}>
                        <span className={classes.aiBadge}>AI</span>
                    </Tooltip>
                )}
                <ExpandMoreIcon
                    className={clsx(classes.expandIcon, expanded && classes.expandIconOpen)}
                    aria-hidden="true"
                />
            </div>
        </div>
    )
}

interface FindingCardActionsProps {
    findingId: string
    onResolve: (id: string) => void
    onDismiss: (id: string) => void
}

const FindingCardActions = ({ findingId, onResolve, onDismiss }: FindingCardActionsProps) => {
    const classes = useStyles()
    return (
        <div
            className={classes.actions}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation() }}
        >
            <Button
                variant="contained"
                size="small"
                className={classes.resolveButton}
                onClick={() => onResolve(findingId)}
                disableElevation
            >
                Resolve
            </Button>
            <Button
                variant="outlined"
                size="small"
                className={classes.dismissButton}
                onClick={() => onDismiss(findingId)}
            >
                Dismiss
            </Button>
        </div>
    )
}

/** Props for {@link SpecIntelligenceFindingCard}. */
export interface SpecIntelligenceFindingCardProps {
    /** The finding to display. */
    finding: SpecIntelligenceFinding
    /** Called when the user clicks Resolve. */
    onResolve: (id: string) => void
    /** Called when the user clicks Dismiss. */
    onDismiss: (id: string) => void
}

/**
 * Collapsed/expanded card for a single SpecIntelligence finding.
 * Expand to reveal Resolve and Dismiss action buttons.
 */
const SpecIntelligenceFindingCard = ({ finding, onResolve, onDismiss }: SpecIntelligenceFindingCardProps) => {
    const classes = useStyles()
    const [expanded, setExpanded] = useState(false)
    const handleToggle = () => setExpanded((prev) => !prev)
    const cleanDescription = finding.description.replace(PARAGRAPH_REF_PATTERN, '$1')
    const descriptionNodes = useMemo(
        () => renderDescriptionWithParagraphRefs(finding.description, classes.referencedSection),
        [finding.description, classes.referencedSection]
    )

    return (
        <div
            className={classes.card}
            onClick={handleToggle}
            role="button"
            tabIndex={0}
            aria-expanded={expanded}
            aria-label={`${finding.sectionNumber} · ${finding.sectionName}: ${cleanDescription}`}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleToggle()
                }
            }}
        >
            <FindingCardHeader finding={finding} expanded={expanded} />
            <Typography className={classes.description}>
                {descriptionNodes}
            </Typography>
            <Collapse in={expanded} unmountOnExit>
                <FindingCardActions
                    findingId={finding.id}
                    onResolve={onResolve}
                    onDismiss={onDismiss}
                />
            </Collapse>
        </div>
    )
}

export default SpecIntelligenceFindingCard
